import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { Lang } from "@/admin/lib/i18nField";

export interface LegalPageContent {
  slug: string;
  eyebrow: string;
  title: string;
  intro: string;
  sections: Array<{ title: string; body: string }>;
}

interface RawSection {
  title?: unknown;
  body?: unknown;
}

function pickI18nString(v: unknown, lang: Lang, fallback: Lang = "fr"): string {
  if (!v || typeof v !== "object") return "";
  const o = v as Record<string, unknown>;
  const value = typeof o[lang] === "string" ? (o[lang] as string) : "";
  if (value.trim()) return value;
  const fb = typeof o[fallback] === "string" ? (o[fallback] as string) : "";
  return fb;
}

function pickI18nSections(v: unknown, lang: Lang, fallback: Lang = "fr"): Array<{ title: string; body: string }> {
  if (!v || typeof v !== "object") return [];
  const o = v as Record<string, unknown>;
  const pickArr = (key: Lang): RawSection[] => (Array.isArray(o[key]) ? (o[key] as RawSection[]) : []);
  const arr = pickArr(lang);
  const useArr = arr.length > 0 ? arr : pickArr(fallback);
  return useArr.map((s) => ({
    title: typeof s?.title === "string" ? s.title : "",
    body: typeof s?.body === "string" ? s.body : "",
  }));
}

export function useLegalPage(slug: string, lang: Lang) {
  return useQuery({
    queryKey: ["legal-page", slug, lang],
    queryFn: async (): Promise<LegalPageContent | null> => {
      const { data, error } = await supabase
        .from("legal_pages")
        .select("slug,eyebrow,title,intro,sections")
        .eq("slug", slug)
        .eq("status", "published")
        .maybeSingle();
      if (error) throw error;
      if (!data) return null;
      return {
        slug: data.slug,
        eyebrow: pickI18nString(data.eyebrow, lang),
        title: pickI18nString(data.title, lang),
        intro: pickI18nString(data.intro, lang),
        sections: pickI18nSections(data.sections, lang),
      };
    },
    staleTime: 60_000,
  });
}
